"use client";

import { ArrowRight, Calendar } from "lucide-react";

import { FadeIn, StaggerContainer, StaggerItem } from "@/components/ui/motion";
import { useLang, useLangPrefix } from "@/contexts/lang";
import type { SectionIds } from "@/data/home";
import type { StrapiArticle } from "@/lib/articles";
import { getCoverUrl } from "@/lib/articles";

type NewsSectionProps = {
	sectionIds: SectionIds;
	articles: StrapiArticle[];
};

export function NewsSection({ sectionIds, articles }: NewsSectionProps) {
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<section
			id={sectionIds.news}
			className="scroll-mt-32 bg-primary text-white py-20 sm:py-24 md:py-32 mt-grain"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="flex flex-col gap-8 md:gap-10 lg:flex-row lg:items-end lg:justify-between mb-12 sm:mb-16 md:mb-20">
					<div className="max-w-2xl">
						<FadeIn>
							{/* Label */}
							<div className="flex items-center gap-4 mb-5 md:mb-6">
								<span className="h-px w-10 sm:w-16 bg-accent/60" />
								<p className="text-label text-accent">{t.news.label}</p>
							</div>

							<h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-white leading-[1.1] tracking-tight mb-5 md:mb-6">
								{t.news.heading}
							</h2>
							<p className="text-refined text-base sm:text-lg text-white/80 max-w-xl">
								{t.news.desc}
							</p>
						</FadeIn>
					</div>

					<FadeIn delay={0.2}>
						<a
							href={`${prefix}/news`}
							className="group inline-flex items-center gap-3 rounded-sm border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-all duration-500 hover:bg-white/10 hover:border-white/25"
						>
							<span>{t.news.seeAll}</span>
							<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
						</a>
					</FadeIn>
				</div>

				{/* Articles grid */}
				<StaggerContainer
					staggerDelay={0.1}
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
				>
					{articles.map((article, idx) => {
						const coverUrl = getCoverUrl(article);
						return (
							<StaggerItem key={article.documentId}>
								<article className="group relative overflow-hidden rounded-sm bg-white shadow-luxury transition-all duration-700 hover:shadow-luxury-xl hover:-translate-y-1 h-full flex flex-col">
									{/* Image area */}
									<div className="relative h-48 sm:h-56 overflow-hidden">
										{coverUrl ? (
											<img
												src={coverUrl}
												alt={article.cover?.alternativeText ?? article.title}
												className="absolute inset-0 w-full h-full object-cover"
											/>
										) : (
											<>
												<div className="absolute inset-0 bg-gradient-to-br from-secondary via-sand-light to-secondary" />
												<div className="absolute inset-0 flex items-center justify-center">
													<div className="text-center">
														<div className="w-14 h-14 mx-auto mb-3 rounded-sm bg-white/80 shadow-luxury-sm flex items-center justify-center">
															<span className="text-2xl opacity-40">📰</span>
														</div>
														<p className="text-xs font-medium text-primary/30 tracking-wide">
															{t.newsPage.newsImage}
														</p>
													</div>
												</div>
											</>
										)}

										{/* Decorative overlay */}
										<div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-primary/5 to-transparent" />

										{/* Category badge */}
										<div className="absolute top-4 left-4">
											<span className="inline-flex items-center rounded-sm bg-white/95 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent shadow-luxury-sm">
												{article.category}
											</span>
										</div>

										{/* Index indicator */}
										<div className="absolute bottom-4 right-4">
											<span className="font-display text-4xl font-light text-white/15">
												{String(idx + 1).padStart(2, "0")}
											</span>
										</div>

										{/* Hover overlay */}
										<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/15 to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
									</div>

									{/* Article content */}
									<div className="p-5 sm:p-6 md:p-7 flex flex-col flex-grow">
										{/* Gold accent line */}
										<div className="w-8 h-px bg-accent/40 mb-4 transition-all duration-500 group-hover:w-12 group-hover:bg-accent" />

										<h3 className="font-display text-lg sm:text-xl font-normal text-primary tracking-tight mb-3 line-clamp-2">
											{article.title}
										</h3>

										<div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
											<Calendar className="h-3.5 w-3.5" />
											<span>{article.date}</span>
										</div>

										<p className="text-sm text-foreground/85 leading-relaxed mb-6 line-clamp-3 flex-grow">
											{article.excerpt}
										</p>

										{/* Read more link */}
										<div className="pt-5 border-t border-primary/5 mt-auto">
											<a
												href={`${prefix}/news/${article.documentId}`}
												className="group/link inline-flex items-center gap-3 text-sm font-medium text-primary transition-colors hover:text-accent"
											>
												<span>{t.news.readMore}</span>
												<span className="flex items-center gap-1">
													<span className="h-px w-6 bg-primary/20 transition-all duration-500 group-hover/link:w-10 group-hover/link:bg-accent" />
													<ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-1" />
												</span>
											</a>
										</div>
									</div>

									{/* Decorative corner */}
									<div className="absolute top-0 right-0 w-12 h-12 pointer-events-none opacity-0 transition-opacity duration-500 group-hover:opacity-100">
										<svg
											viewBox="0 0 48 48"
											fill="none"
											className="w-full h-full"
											aria-hidden="true"
										>
											<path
												d="M48 0 L48 48 L0 48"
												stroke="rgba(201, 161, 90, 0.3)"
												strokeWidth="1"
												fill="none"
											/>
										</svg>
									</div>
								</article>
							</StaggerItem>
						);
					})}
				</StaggerContainer>
			</div>
		</section>
	);
}
